import { format as formatDate } from 'date-fns';
import { ArticleFormat, ArticleStatus } from '../../__generated__/queries-web';
import { RecipeStatus } from '../../__generated__/queries-recipe';
import { statuses } from '../../utils/statuses';
import { Format } from './FiltersDrawer/utils';

export interface ExportRowItem {
  publishedAt: string | null | undefined;
  format: Format | undefined;
  brandKey: string;
  title: string | null;
  categories: { id: string; label: string }[];
  tags: { id: string; label: string }[];
  qualifiers: { id: string; title: string }[];
  status: ArticleStatus | RecipeStatus | null | undefined;
  href: string | null | undefined;
  siteUrl?: string;
}

const formatToString = (format: Format | undefined): string => {
  switch (format) {
    case Format.Article:
      return 'ARTICLE';
    case Format.Slideshow:
      return 'DIAPO';
    case Format.Recipe:
      return 'RECETTE';
    default:
      return '';
  }
};

const statusToString = (
  status: ArticleStatus | RecipeStatus | null | undefined,
): string => {
  if (!status) return '';
  return statuses(ArticleFormat.Rich)[status]?.label || status;
};

const formatDateString = (dateString: string | null | undefined): string => {
  if (!dateString) return '';
  try {
    return formatDate(new Date(dateString), 'dd/MM/yyyy HH:mm:ss');
  } catch {
    return '';
  }
};

const getOneUrl = (href: string | null | undefined): string => {
  if (!href) return '';

  // If href is already a full URL (like recipe URLs), return it as-is
  if (href.startsWith('http')) {
    return href;
  }

  // For relative paths (like article paths), add the base URL and hash
  const baseUrl = window.location.origin;
  return `${baseUrl}/#${href}`;
};

const getSiteUrl = (siteUrl?: string): string => {
  return siteUrl || '';
};

const escapeCSVField = (field: string): string => {
  if (field.includes(',') || field.includes('"') || field.includes('\n')) {
    return `"${field.replace(/"/g, '""')}"`;
  }
  return field;
};

export const exportToCSV = (
  data: ExportRowItem[],
  filename = 'articles.csv',
): void => {
  const headers = [
    'publishedAt',
    'Status',
    'Format',
    'brandKey',
    'Title',
    'Url ONE',
    'Url Site',
    'Category',
    'Tag',
    'Qualifiers',
  ];

  const csvContent = [
    headers.join(','),
    ...data.map((row) =>
      [
        escapeCSVField(formatDateString(row.publishedAt)),
        escapeCSVField(statusToString(row.status)),
        escapeCSVField(formatToString(row.format)),
        escapeCSVField(row.brandKey),
        escapeCSVField(row.title || ''),
        escapeCSVField(getOneUrl(row.href)),
        escapeCSVField(getSiteUrl(row.siteUrl)),
        escapeCSVField(row.categories.map((cat) => cat.label).join(', ')),
        escapeCSVField(row.tags.map((tag) => tag.label).join(', ')),
        escapeCSVField(
          row.qualifiers.map((qualifier) => qualifier.title).join(', '),
        ),
      ].join(','),
    ),
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.setAttribute('style', 'visibility: hidden');

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
